/**
 * Rechnungen prüfen — E10 Rechnungskontrolle finally operable (Story UI-U4).
 *
 * Table (Sebastian's prototype) → invoice decision peek → real E10 decisions.
 * List via useInvoiceRows (live GET /invoices, mock fallback). Presets, sum row,
 * client CSV export, and a guarded "Alle freigeben" bulk fan-out over the
 * as-built override/handle endpoints (K3/blocked NEVER bulk-released).
 * Aufgaben deep-link: /rechnungen?invoice=<uuid> opens the peek on that row.
 *
 * [Source: brokly-design-reference/components/brokly/rechnungen-view.tsx]
 * [Source: docs/stories/ui-u4-rechnungskontrolle.md AC1/AC3/AC5]
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Download, ShieldCheck } from 'lucide-react';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { StatusDot } from '../../components/ui/StatusDot';
import { Button } from '../../components/ui/Button';
import { TableCell } from '../../components/ui/Table';
import { EntityLink } from '../../components/ui/EntityLink';
import { cn } from '../../lib/cn';
import { chf } from '../../lib/format';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { KategorieBadge } from './components/KategorieBadge';
import { InvoicePeek } from './components/InvoicePeek';
import { useInvoiceRows } from './hooks/useInvoiceRows';
import {
  INVOICE_PRESETS,
  STATUS_LABEL,
  STATUS_TONE,
  TYP_LABEL,
  TYP_SHORT,
  toCsv,
} from './api/invoices-api';
import type { InvoiceRow } from './types';
import { rechnungenRoute } from '../../routeTree.gen';

const MIN_REASON = 10;
const num = 'text-right mono-num';

/** Differenz colour: within-tolerance (K0/K1/uncat) muted; K2/K3 warning tone. */
function diffMuted(row: InvoiceRow): boolean {
  return row.kategorie === null || row.kategorie === 'K0' || row.kategorie === 'K1';
}

/** Signed absolute difference without the CHF prefix (mono). */
function diffLabel(d: number): string {
  return `${d >= 0 ? '+' : '−'}${chf(Math.abs(d)).replace('CHF ', '')}`;
}

/**
 * Column factory — takes the Kunde-navigation callback so the Kunde cell is a
 * REAL entity link (app-wide convention: EntityLink always carries onActivate).
 * Rows without a resolved subject render plain text (no dead control; the
 * row-click still opens the peek). [adversarial-review fix: dead EntityLink]
 */
function makeColumns(onOpenKunde: (id: string) => void): ColumnDef<InvoiceRow>[] {
  return [
  {
    accessorKey: 'nr',
    header: 'Rechnungs-Nr.',
    cell: ({ row }) => <span className="mono-num font-medium">{row.original.nr}</span>,
  },
  {
    accessorKey: 'kunde',
    header: 'Kunde',
    cell: ({ row }) => (
      <div className="leading-tight">
        <div>
          {row.original.subjectId ? (
            <EntityLink onActivate={() => onOpenKunde(row.original.subjectId!)}>
              {row.original.kunde}
            </EntityLink>
          ) : (
            <span className="font-medium">{row.original.kunde}</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{row.original.versicherer}</div>
      </div>
    ),
  },
  {
    accessorKey: 'typ',
    header: 'Typ',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground" title={TYP_LABEL[row.original.typ]}>
        {TYP_SHORT[row.original.typ]}
      </span>
    ),
  },
  {
    accessorKey: 'erwartet',
    header: 'Erwartet',
    cell: ({ row }) => <div className={num}>{chf(row.original.erwartet)}</div>,
  },
  {
    accessorKey: 'rechnung',
    header: 'Rechnung',
    cell: ({ row }) => <div className={num}>{chf(row.original.rechnung)}</div>,
  },
  {
    accessorKey: 'differenz',
    header: 'Differenz',
    sortingFn: (a, b) => Math.abs(a.original.pct) - Math.abs(b.original.pct),
    cell: ({ row }) => {
      const muted = diffMuted(row.original);
      return (
        <div className={cn(num, 'font-medium', muted ? 'text-muted-foreground' : 'text-status-warning')}>
          <div>{diffLabel(row.original.differenz)}</div>
          <div className="text-xs opacity-80">{row.original.pct.toFixed(1)} %</div>
        </div>
      );
    },
  },
  {
    accessorKey: 'kategorie',
    header: 'Kategorie',
    cell: ({ row }) =>
      row.original.kategorie ? (
        <KategorieBadge kategorie={row.original.kategorie} pflichtregel={row.original.pflichtregel} />
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
  {
    accessorKey: 'wartezeitTage',
    header: 'Wartezeit',
    cell: ({ row }) => (
      <span className="text-muted-foreground [font-variant-numeric:tabular-nums]">
        {row.original.wartezeitTage} T
      </span>
    ),
  },
  {
    accessorKey: 'rawStatus',
    header: 'Status',
    cell: ({ row }) => (
      <StatusDot tone={STATUS_TONE[row.original.rawStatus]} label={STATUS_LABEL[row.original.rawStatus]} />
    ),
  },
  ];
}

/** Bulk-release eligibility: in_review (override→K1) or checked K0/K1 (handle). NEVER K3/blocked. */
function isBulkEligible(row: InvoiceRow): boolean {
  if (row.rawStatus === 'in_review') return true;
  if (row.rawStatus === 'checked' && (row.kategorie === 'K0' || row.kategorie === 'K1')) return true;
  return false;
}

export function InvoicesScreen() {
  const { data = [], isLoading, isError } = useInvoiceRows();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { invoice: deepLinkId } = rechnungenRoute.useSearch();

  const [activeView, setActiveView] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Entity navigation (app-wide EntityLink convention): Kunde → customer detail,
  // Police → policy detail. The peek is presentational, so it delegates its
  // Police link back to the screen (keeps the peek router-free for its tests).
  const columns = useMemo(
    () => makeColumns((id) => void navigate({ to: '/customers/$id', params: { id } })),
    [navigate],
  );
  const openPolice = (policyId: string) =>
    void navigate({ to: '/policen/$id', params: { id: policyId } });

  function showFeedback(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 2500);
  }
  function showError(msg: string) {
    setErrorFeedback(msg);
    window.setTimeout(() => setErrorFeedback(null), 3500);
  }

  // Preset + search + default Wartezeit-desc sort (client-side, over server fields).
  const visible = useMemo(() => {
    const preset = INVOICE_PRESETS[activeView] ?? INVOICE_PRESETS[0]!;
    const q = search.trim().toLowerCase();
    const filtered = data.filter((r) => {
      if (!preset.match(r)) return false;
      if (!q) return true;
      return `${r.nr} ${r.kunde} ${r.versicherer} ${r.police}`.toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) => b.wartezeitTage - a.wartezeitTage);
  }, [data, activeView, search]);

  const selectedIndex = visible.findIndex((r) => r.id === selectedId);
  const selected = selectedIndex >= 0 ? visible[selectedIndex]! : null;

  const views = INVOICE_PRESETS.map((p) => ({
    label: p.label,
    count: data.filter(p.match).length,
  }));

  const sumErwartet = visible.reduce((acc, r) => acc + r.erwartet, 0);
  const sumRechnung = visible.reduce((acc, r) => acc + r.rechnung, 0);
  const sumDifferenz = visible.reduce((acc, r) => acc + r.differenz, 0);

  const eligible = useMemo(() => visible.filter(isBulkEligible), [visible]);

  // Deep-link: open the peek on ?invoice= once the rows contain that id — but
  // ONLY ONCE per distinct id. Re-applying on every `data` change (background
  // refetch / post-decision invalidation) would fight auto-advance + J/K nav and
  // snap the selection back to the deep-linked row. [adversarial-review fix]
  const appliedDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkId) {
      appliedDeepLink.current = null;
      return;
    }
    if (appliedDeepLink.current === deepLinkId) return;
    if (data.some((r) => r.id === deepLinkId)) {
      setSelectedId(deepLinkId);
      appliedDeepLink.current = deepLinkId;
    }
  }, [deepLinkId, data]);

  // Keyboard: J/K navigate the visible list, Esc closes (suppressed while typing).
  useEffect(() => {
    if (selectedId === null) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') closePeek();
      else if (e.key === 'j' || e.key === 'J') gotoNext();
      else if (e.key === 'k' || e.key === 'K') gotoPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, visible]);

  function gotoNext() {
    if (selectedIndex >= 0 && selectedIndex < visible.length - 1) {
      setSelectedId(visible[selectedIndex + 1]!.id);
    }
  }
  function gotoPrev() {
    if (selectedIndex > 0) setSelectedId(visible[selectedIndex - 1]!.id);
  }
  function closePeek() {
    setSelectedId(null);
    if (deepLinkId) {
      void navigate({ to: '/rechnungen', search: {} });
    }
  }

  function exportCsv() {
    const blob = new Blob([toCsv(visible)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rechnungen-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <>
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 rounded-lg border border-status-success-border bg-status-success-bg px-4 py-2.5 text-sm text-status-success shadow-md"
        >
          {feedback}
        </div>
      )}
      {errorFeedback && (
        <div
          role="alert"
          className="fixed bottom-16 right-4 z-50 rounded-lg border border-status-danger-border bg-status-danger-bg px-4 py-2.5 text-sm text-status-danger shadow-md"
        >
          {errorFeedback}
        </div>
      )}

      <ListView
        title="Rechnungen prüfen"
        views={views}
        activeView={activeView}
        onViewChange={(i) => setActiveView(i)}
        searchValue={search}
        onSearchChange={setSearch}
        count={`${visible.length} von ${data.length}`}
        searchPlaceholder="Nr., Kunde, Versicherer, Police …"
        rightAction={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="size-3.5" /> Exportieren
            </Button>
            {eligible.length > 0 && (
              <Button
                size="sm"
                className="bg-status-success text-white hover:bg-status-success/90"
                onClick={() => setBulkOpen(true)}
              >
                <ShieldCheck className="size-3.5" /> Alle freigeben ({eligible.length})
              </Button>
            )}
          </div>
        }
      >
        {isLoading ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Lade Rechnungen …
          </div>
        ) : isError ? (
          <div role="alert" className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Rechnungen konnten nicht geladen werden.
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Keine Rechnungen in diesem Filter.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={visible}
            onRowClick={(r) => setSelectedId(r.id)}
            summary={
              <>
                <TableCell className="font-medium">Summe</TableCell>
                <TableCell />
                <TableCell />
                <TableCell className={`font-medium ${num}`}>{chf(sumErwartet)}</TableCell>
                <TableCell className={`font-medium ${num}`}>{chf(sumRechnung)}</TableCell>
                <TableCell className={`font-medium ${num}`}>{diffLabel(sumDifferenz)}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </>
            }
          />
        )}
      </ListView>

      {selected && (
        <InvoicePeek
          key={selected.id}
          row={selected}
          index={selectedIndex}
          total={visible.length}
          onClose={closePeek}
          onOpenPolice={openPolice}
          onPrev={gotoPrev}
          onNext={() => {
            if (selectedIndex >= 0 && selectedIndex < visible.length - 1) gotoNext();
            else closePeek();
          }}
          onToast={showFeedback}
          onError={showError}
        />
      )}

      {bulkOpen && (
        <BulkReleaseDialog
          eligible={eligible}
          onClose={() => setBulkOpen(false)}
          onDone={(released, failed) => {
            setBulkOpen(false);
            void qc.invalidateQueries({ queryKey: qk.invoices({ mode: isLiveData() ? 'live' : 'mock' }) });
            if (failed > 0) showError(`${released} freigegeben · ${failed} fehlgeschlagen`);
            else showFeedback(`${released} freigegeben`);
          }}
        />
      )}
    </>
  );
}

/**
 * Bulk-release confirm dialog + sequential fan-out. ONE shared mandatory reason
 * for the in_review (override→K1) leg; the checked K0/K1 rows go through
 * reason-free handle. K3/blocked are excluded (stated in the dialog). Per-row
 * try/catch — one failure never aborts the rest.
 */
function BulkReleaseDialog({
  eligible,
  onClose,
  onDone,
}: {
  eligible: InvoiceRow[];
  onClose: () => void;
  onDone: (released: number, failed: number) => void;
}) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [running, setRunning] = useState(false);

  const needsReason = eligible.some((r) => r.rawStatus === 'in_review');

  async function confirm() {
    if (needsReason && reason.trim().length < MIN_REASON) {
      setReasonError(true);
      return;
    }
    setRunning(true);
    let released = 0;
    let failed = 0;
    for (const row of eligible) {
      try {
        if (row.rawStatus === 'in_review') {
          await apiClient.adminInvoiceCheck.override(row.id, { newCategory: 'K1', reason: reason.trim() });
        } else {
          await apiClient.adminInvoiceCheck.handle(row.id);
        }
        released += 1;
      } catch {
        failed += 1;
      }
    }
    setRunning(false);
    onDone(released, failed);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl">
        <h2 className="text-base font-semibold text-foreground">Alle freigeben</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {eligible.length} Rechnung(en) werden freigegeben. K3 / blockierte Rechnungen sind
          ausgenommen.
        </p>
        {needsReason && (
          <div className="mt-3 flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Begründung (Pflicht)</label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim().length >= MIN_REASON) setReasonError(false);
              }}
              rows={2}
              placeholder="Gemeinsame Begründung der Freigabe …"
              className={cn(
                'w-full resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
                reasonError ? 'border-status-danger' : 'border-border',
              )}
            />
            {reasonError && (
              <span role="alert" className="text-xs text-status-danger">
                Bitte eine Begründung erfassen (mind. {MIN_REASON} Zeichen).
              </span>
            )}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={running}>
            Abbrechen
          </Button>
          <Button
            className="bg-status-success text-white hover:bg-status-success/90"
            onClick={confirm}
            disabled={running}
          >
            {running ? 'Wird freigegeben …' : `${eligible.length} freigeben`}
          </Button>
        </div>
      </div>
    </div>
  );
}
